import React, { useState, useEffect } from 'react';
import { AlertTriangle, Brain, Clock, TrendingUp, Settings, Home, BarChart3, Activity, Keyboard, MousePointer, Layers, Heart } from 'lucide-react';

const NeuroSyncApp = () => {
  const [currentScreen, setCurrentScreen] = useState('splash');
  const [onboardingStep, setOnboardingStep] = useState(0);
  const [focusTimer, setFocusTimer] = useState(25 * 60);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [breakTimer, setBreakTimer] = useState(20);
  const [cognitiveLoad, setCognitiveLoad] = useState(25);
  const [breathingPhase, setBreathingPhase] = useState(0);
  const [lastBreakTime, setLastBreakTime] = useState(Date.now());
  const [tasks, setTasks] = useState([
    { id: 1, text: 'Finish Notes', completed: false },
    { id: 2, text: 'Study Algorithms', completed: false },
    { id: 3, text: 'Write Summary', completed: false }
  ]);
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [newTaskText, setNewTaskText] = useState('');
  const [isAddingTask, setIsAddingTask] = useState(false);
  
  // Settings state
  const [theme, setTheme] = useState('dark');
  const [fontSize, setFontSize] = useState('medium');
  const [isSimplifiedUI, setIsSimplifiedUI] = useState(false);
  const [colorBlindMode, setColorBlindMode] = useState(false);

  // Timer logic
  useEffect(() => {
    if (isTimerRunning && focusTimer > 0) {
      const interval = setInterval(() => {
        setFocusTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [isTimerRunning, focusTimer]);

  // Break timer logic
  useEffect(() => {
    if (currentScreen === 'microBreak' && breakTimer > 0) {
      const interval = setInterval(() => {
        setBreakTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [currentScreen, breakTimer]);

  // Breathing animation
  useEffect(() => {
    if (currentScreen === 'focusMode' || currentScreen === 'microBreak') {
      const interval = setInterval(() => {
        setBreathingPhase(prev => (prev + 1) % 100);
      }, 40);
      return () => clearInterval(interval);
    }
  }, [currentScreen]);

  // Natural cognitive load fluctuation - always running
  useEffect(() => {
    const interval = setInterval(() => {
      setCognitiveLoad(prev => {
        const timeSinceBreak = (Date.now() - lastBreakTime) / 1000 / 60; // minutes
        const breakPenalty = Math.min(timeSinceBreak * 0.5, 15); // up to +15 from no breaks
        
        // Natural fluctuation between -0.5 and +1.5
        const naturalChange = (Math.random() * 2) - 0.5;
        
        // Gradual increase over time (slower when not in focus mode)
        const timeIncrease = isTimerRunning ? 0.3 : 0.1;
        
        let newLoad = prev + naturalChange + timeIncrease + (breakPenalty * 0.01);
        
        // Keep within bounds
        newLoad = Math.max(15, Math.min(95, newLoad));
        
        // Trigger alert if too high
        if (newLoad > 75 && currentScreen === 'dashboard') {
          setCurrentScreen('cognitiveAlert');
          setIsTimerRunning(false);
        }
        
        return newLoad;
      });
    }, 3000); // Update every 3 seconds for smoother changes
    
    return () => clearInterval(interval);
  }, [currentScreen, isTimerRunning, lastBreakTime]);

  // Remove old cognitive load effect
  // useEffect(() => {
  //   if (currentScreen === 'dashboard' && isTimerRunning) {
  //     const interval = setInterval(() => {
  //       setCognitiveLoad(prev => Math.min(100, prev + Math.random() * 2));
  //       if (cognitiveLoad > 75) {
  //         setCurrentScreen('cognitiveAlert');
  //         setIsTimerRunning(false);
  //       }
  //     }, 5000);
  //     return () => clearInterval(interval);
  //   }
  // }, [currentScreen, isTimerRunning, cognitiveLoad]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const getLoadStatus = () => {
    if (cognitiveLoad < 40) return { text: 'Low', color: 'text-green-400' };
    if (cognitiveLoad < 70) return { text: 'Stable', color: 'text-green-400' };
    return { text: 'High', color: 'text-red-400' };
  };

  const handleTaskCompletion = (taskId) => {
    const task = tasks.find(t => t.id === taskId);
    setTasks(tasks.map(t => 
      t.id === taskId ? {...t, completed: !t.completed} : t
    ));
    
    // Completing a task increases cognitive load (mental effort)
    if (!task.completed) {
      setCognitiveLoad(prev => Math.min(95, prev + 8));
    } else {
      // Uncompleting reduces it slightly
      setCognitiveLoad(prev => Math.max(15, prev - 5));
    }
  };

  // Get theme classes
  const getThemeClasses = () => {
    const themes = {
      dark: 'bg-gradient-to-b from-gray-900 to-gray-800 text-white',
      light: 'bg-gradient-to-b from-gray-50 to-gray-100 text-gray-900',
      highContrast: 'bg-black text-white'
    };
    return themes[theme] || themes.dark;
  };

  const getFontSizeClass = () => {
    const sizes = {
      small: 'text-sm',
      medium: 'text-base',
      large: 'text-lg'
    };
    return sizes[fontSize] || sizes.medium;
  };

  const getColorScheme = () => {
    if (colorBlindMode) {
      return {
        primary: '#0ea5e9', // Blue
        success: '#f59e0b', // Amber (instead of green)
        danger: '#dc2626',
        accent: '#8b5cf6'
      };
    }
    return {
      primary: '#3b82f6',
      success: '#10b981',
      danger: '#ef4444',
      accent: '#8b5cf6'
    };
  };

  // Splash Screen
  if (currentScreen === 'splash') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 flex flex-col items-center justify-center p-8 text-white">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-light mb-12 text-blue-200">Onboarding</h1>
          <div className="mb-12">
            <div className="relative w-48 h-48 mx-auto">
              <Brain className="w-full h-full text-blue-400 opacity-80" strokeWidth={0.5} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-32 h-32 bg-blue-500 rounded-full blur-3xl opacity-50 animate-pulse"></div>
              </div>
            </div>
          </div>
          <h2 className="text-3xl font-light mb-6">Understand your cognitive load in real time.</h2>
          <div className="flex gap-2 justify-center mb-12">
            <div className="w-2 h-2 rounded-full bg-blue-500"></div>
            <div className="w-2 h-2 rounded-full bg-gray-600"></div>
            <div className="w-2 h-2 rounded-full bg-gray-600"></div>
          </div>
          <button 
            onClick={() => setCurrentScreen('login')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-12 py-4 rounded-full text-lg transition-all"
          >
            Next →
          </button>
        </div>
      </div>
    );
  }

  // Login Screen
  if (currentScreen === 'login') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex flex-col items-center justify-center p-8 text-white">
        <div className="text-center max-w-md w-full">
          <h1 className="text-4xl font-light mb-16">Login to continue</h1>
          <div className="space-y-4 mb-8">
            <button className="w-full bg-white text-gray-800 py-4 rounded-full flex items-center justify-center gap-3 hover:bg-gray-100 transition-all">
              <div className="w-5 h-5 bg-gradient-to-r from-blue-500 via-red-500 to-yellow-500 rounded"></div>
              Continue with Google
            </button>
            <button className="w-full bg-white text-gray-800 py-4 rounded-full flex items-center justify-center gap-3 hover:bg-gray-100 transition-all">
              <div className="w-5 h-5 bg-black rounded"></div>
              Continue with Apple
            </button>
          </div>
          <button 
            onClick={() => setCurrentScreen('permissions')}
            className="text-gray-400 hover:text-white transition-all"
          >
            Continue without account
          </button>
        </div>
      </div>
    );
  }

  // Settings Screen
  if (currentScreen === 'settings') {
    const colors = getColorScheme();
    const bgClass = theme === 'light' ? 'bg-white' : theme === 'highContrast' ? 'bg-black' : 'bg-gray-800';
    const textClass = theme === 'light' ? 'text-gray-900' : 'text-white';
    const secondaryBg = theme === 'light' ? 'bg-gray-100' : theme === 'highContrast' ? 'bg-gray-900' : 'bg-gray-700';
    
    return (
      <div className={`min-h-screen ${getThemeClasses()} p-6 pb-24 ${getFontSizeClass()}`}>
        <div className="max-w-md mx-auto">
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl font-light">Interface & Accessibility</h1>
            <button 
              onClick={() => setCurrentScreen('dashboard')}
              className="text-blue-400 hover:text-blue-300"
            >
              Done
            </button>
          </div>

          <div className="space-y-6">
            {/* Theme Selection */}
            <div className={`${bgClass} rounded-2xl p-6`}>
              <h3 className="font-semibold mb-4">Theme</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setTheme('dark')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg ${theme === 'dark' ? secondaryBg : 'bg-transparent'} border ${theme === 'dark' ? 'border-blue-500' : 'border-gray-600'}`}
                >
                  <span>Dark Mode</span>
                  {theme === 'dark' && <span style={{color: colors.primary}}>✓</span>}
                </button>
                <button
                  onClick={() => setTheme('light')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg ${theme === 'light' ? 'bg-gray-200' : 'bg-transparent'} border ${theme === 'light' ? 'border-blue-500' : 'border-gray-600'}`}
                >
                  <span>Light Mode</span>
                  {theme === 'light' && <span style={{color: colors.primary}}>✓</span>}
                </button>
                <button
                  onClick={() => setTheme('highContrast')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg ${theme === 'highContrast' ? 'bg-gray-900' : 'bg-transparent'} border ${theme === 'highContrast' ? 'border-white' : 'border-gray-600'}`}
                >
                  <span>High Contrast</span>
                  {theme === 'highContrast' && <span style={{color: colors.primary}}>✓</span>}
                </button>
              </div>
            </div>

            {/* Font Size */}
            <div className={`${bgClass} rounded-2xl p-6`}>
              <h3 className="font-semibold mb-4">Font Size</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setFontSize('small')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg ${fontSize === 'small' ? secondaryBg : 'bg-transparent'} border ${fontSize === 'small' ? 'border-blue-500' : 'border-gray-600'}`}
                >
                  <span className="text-sm">Small</span>
                  {fontSize === 'small' && <span style={{color: colors.primary}}>✓</span>}
                </button>
                <button
                  onClick={() => setFontSize('medium')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg ${fontSize === 'medium' ? secondaryBg : 'bg-transparent'} border ${fontSize === 'medium' ? 'border-blue-500' : 'border-gray-600'}`}
                >
                  <span className="text-base">Medium</span>
                  {fontSize === 'medium' && <span style={{color: colors.primary}}>✓</span>}
                </button>
                <button
                  onClick={() => setFontSize('large')}
                  className={`w-full flex items-center justify-between p-4 rounded-lg ${fontSize === 'large' ? secondaryBg : 'bg-transparent'} border ${fontSize === 'large' ? 'border-blue-500' : 'border-gray-600'}`}
                >
                  <span className="text-lg">Large</span>
                  {fontSize === 'large' && <span style={{color: colors.primary}}>✓</span>}
                </button>
              </div>
            </div>

            {/* Simplified UI Toggle */}
            <div className={`${bgClass} rounded-2xl p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">Simplified UI</h3>
                  <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>Reduce visual clutter for better focus</p>
                </div>
                <button
                  onClick={() => setIsSimplifiedUI(!isSimplifiedUI)}
                  className={`w-14 h-8 rounded-full transition-all ${isSimplifiedUI ? 'bg-blue-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-all ${isSimplifiedUI ? 'ml-7' : 'ml-1'}`}></div>
                </button>
              </div>
            </div>

            {/* Color Blind Mode Toggle */}
            <div className={`${bgClass} rounded-2xl p-6`}>
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold mb-1">Color Blind Mode</h3>
                  <p className={`text-sm ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>Uses blue/amber instead of green/red</p>
                </div>
                <button
                  onClick={() => setColorBlindMode(!colorBlindMode)}
                  className={`w-14 h-8 rounded-full transition-all ${colorBlindMode ? 'bg-blue-500' : 'bg-gray-600'}`}
                >
                  <div className={`w-6 h-6 bg-white rounded-full transition-all ${colorBlindMode ? 'ml-7' : 'ml-1'}`}></div>
                </button>
              </div>
            </div>

            {/* Preview */}
            <div className={`${bgClass} rounded-2xl p-6`}>
              <h3 className="font-semibold mb-4">Preview</h3>
              <div className="flex gap-4">
                <div className="w-12 h-12 rounded-full" style={{backgroundColor: colors.success}}></div>
                <div className="w-12 h-12 rounded-full" style={{backgroundColor: colors.primary}}></div>
                <div className="w-12 h-12 rounded-full" style={{backgroundColor: colors.danger}}></div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Nav */}
        <div className={`fixed bottom-0 left-0 right-0 ${theme === 'light' ? 'bg-white border-gray-300' : 'bg-gray-900 border-gray-700'} border-t flex justify-around py-4`}>
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('dashboard')}>
            <Home className={`w-6 h-6 ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`} />
            <span className={`text-xs ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>Home</span>
          </button>
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('analytics')}>
            <BarChart3 className={`w-6 h-6 ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`} />
            <span className={`text-xs ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>Analytics</span>
          </button>
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('settings')}>
            <Settings className="w-6 h-6 text-blue-500" />
            <span className="text-xs text-blue-500">Settings</span>
          </button>
        </div>
      </div>
    );
  }

  // Permissions Screen
  if (currentScreen === 'permissions') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex flex-col items-center justify-center p-8 text-white">
        <div className="max-w-md w-full">
          <h1 className="text-3xl font-light mb-12">We need these signals:</h1>
          <div className="space-y-6 mb-12">
            <div className="flex items-center gap-4 text-lg">
              <Keyboard className="w-6 h-6 text-gray-400" />
              <span>Typing speed</span>
            </div>
            <div className="flex items-center gap-4 text-lg">
              <MousePointer className="w-6 h-6 text-gray-400" />
              <span>Interaction patterns</span>
            </div>
            <div className="flex items-center gap-4 text-lg opacity-50">
              <Layers className="w-6 h-6 text-gray-400" />
              <span className="line-through">App switching data</span>
            </div>
            <div className="flex items-center gap-4 text-lg">
              <Heart className="w-6 h-6 text-gray-400" />
              <span>Smartwatch HR (opt.)</span>
            </div>
          </div>
          <button 
            onClick={() => setCurrentScreen('dashboard')}
            className="w-full bg-white text-gray-800 py-4 rounded-full text-lg hover:bg-gray-100 transition-all mb-4"
          >
            Enable Permissions
          </button>
          <button 
            onClick={() => setCurrentScreen('dashboard')}
            className="w-full text-gray-400 hover:text-white transition-all"
          >
            Skip for now
          </button>
        </div>
      </div>
    );
  }

  // Dashboard
  if (currentScreen === 'dashboard') {
    const loadStatus = getLoadStatus();
    const colors = getColorScheme();
    const bgClass = theme === 'light' ? 'bg-white' : theme === 'highContrast' ? 'bg-black' : 'bg-gray-800';
    const textClass = theme === 'light' ? 'text-gray-900' : 'text-white';
    
    return (
      <div className={`min-h-screen ${getThemeClasses()} p-6 pb-32 ${getFontSizeClass()}`}>
        <div className="max-w-md mx-auto">
          <h1 className="text-xl font-light mb-8 text-center">Dashboard (Cognitive Load)</h1>
          
          {/* Cognitive Load Circle - Hide in simplified mode */}
          {!isSimplifiedUI && (
            <>
              <div className="relative w-48 h-48 mx-auto mb-6">
                <svg className="w-full h-full transform -rotate-90">
                  <circle cx="96" cy="96" r="80" stroke={theme === 'light' ? '#e5e7eb' : '#374151'} strokeWidth="12" fill="none" />
                  <circle 
                    cx="96" 
                    cy="96" 
                    r="80" 
                    stroke={colors.success} 
                    strokeWidth="12" 
                    fill="none"
                    strokeDasharray={`${cognitiveLoad * 5.02} 502`}
                    className="transition-all duration-300"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-4xl font-light">{loadStatus.text}</span>
                </div>
              </div>

              <div className="text-center mb-8">
                <span className={theme === 'light' ? 'text-gray-600' : 'text-gray-400'}>Status: </span>
                <span className={loadStatus.color} style={{color: loadStatus.text === 'High' ? colors.danger : colors.success}}>● {loadStatus.text}</span>
              </div>
            </>
          )}

          {/* Simplified mode - just status text */}
          {isSimplifiedUI && (
            <div className="text-center mb-8">
              <div className="text-5xl font-light mb-4">{loadStatus.text}</div>
              <span className={theme === 'light' ? 'text-gray-600' : 'text-gray-400'}>Cognitive Load</span>
            </div>
          )}

          {/* Tasks */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg">Tasks</h2>
              <button 
                onClick={() => setIsAddingTask(true)}
                className="text-blue-400 text-sm hover:text-blue-300"
                style={{color: colors.primary}}
              >
                + Add Task
              </button>
            </div>
            <div className="space-y-3">
              {tasks.map(task => (
                <div key={task.id} className={`flex items-center gap-3 ${theme === 'light' ? 'text-gray-900' : 'text-gray-300'}`}>
                  <div 
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center cursor-pointer flex-shrink-0`}
                    style={{
                      backgroundColor: task.completed ? colors.success : 'transparent',
                      borderColor: task.completed ? colors.success : (theme === 'light' ? '#9ca3af' : '#6b7280')
                    }}
                    onClick={() => handleTaskCompletion(task.id)}
                  >
                    {task.completed && <span className="text-white text-xs">✓</span>}
                  </div>
                  
                  {editingTaskId === task.id ? (
                    <input
                      type="text"
                      value={newTaskText}
                      onChange={(e) => setNewTaskText(e.target.value)}
                      onBlur={() => {
                        if (newTaskText.trim()) {
                          setTasks(tasks.map(t => 
                            t.id === task.id ? {...t, text: newTaskText} : t
                          ));
                        }
                        setEditingTaskId(null);
                        setNewTaskText('');
                      }}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          if (newTaskText.trim()) {
                            setTasks(tasks.map(t => 
                              t.id === task.id ? {...t, text: newTaskText} : t
                            ));
                          }
                          setEditingTaskId(null);
                          setNewTaskText('');
                        }
                      }}
                      className={`flex-1 px-3 py-1 rounded outline-none ${theme === 'light' ? 'bg-gray-200 text-gray-900' : 'bg-gray-700 text-white'}`}
                      autoFocus
                    />
                  ) : (
                    <span 
                      className={`flex-1 cursor-pointer ${task.completed ? 'line-through' : ''}`}
                      onDoubleClick={() => {
                        setEditingTaskId(task.id);
                        setNewTaskText(task.text);
                      }}
                    >
                      {task.text}
                    </span>
                  )}
                  
                  <button
                    onClick={() => setTasks(tasks.filter(t => t.id !== task.id))}
                    className="hover:text-red-300 text-sm"
                    style={{color: colors.danger}}
                  >
                    ×
                  </button>
                </div>
              ))}
              
              {isAddingTask && (
                <div className={`flex items-center gap-3 ${theme === 'light' ? 'text-gray-900' : 'text-gray-300'}`}>
                  <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0`} style={{borderColor: theme === 'light' ? '#9ca3af' : '#6b7280'}}></div>
                  <input
                    type="text"
                    placeholder="New task..."
                    value={newTaskText}
                    onChange={(e) => setNewTaskText(e.target.value)}
                    onBlur={() => {
                      if (newTaskText.trim()) {
                        setTasks([...tasks, {
                          id: Date.now(),
                          text: newTaskText,
                          completed: false
                        }]);
                      }
                      setIsAddingTask(false);
                      setNewTaskText('');
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && newTaskText.trim()) {
                        setTasks([...tasks, {
                          id: Date.now(),
                          text: newTaskText,
                          completed: false
                        }]);
                        setIsAddingTask(false);
                        setNewTaskText('');
                      }
                    }}
                    className={`flex-1 px-3 py-1 rounded outline-none ${theme === 'light' ? 'bg-gray-200 text-gray-900' : 'bg-gray-700 text-white'}`}
                    autoFocus
                  />
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-3">
            <button 
              onClick={() => {
                setCurrentScreen('focusMode');
                setIsTimerRunning(true);
                setFocusTimer(25 * 60);
              }}
              className={`w-full py-4 rounded-full text-lg transition-all ${theme === 'light' ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-white text-gray-900 hover:bg-gray-100'}`}
            >
              Focus Mode
            </button>
            <button 
              onClick={() => {
                setCurrentScreen('microBreak');
                setBreakTimer(20);
              }}
              className={`w-full py-4 rounded-full text-lg transition-all ${theme === 'light' ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-white text-gray-900 hover:bg-gray-100'}`}
            >
              Break
            </button>
            <button 
              onClick={() => setIsSimplifiedUI(!isSimplifiedUI)}
              className={`w-full py-4 rounded-full text-lg transition-all ${theme === 'light' ? 'bg-gray-900 text-white hover:bg-gray-800' : 'bg-white text-gray-900 hover:bg-gray-100'}`}
            >
              {isSimplifiedUI ? 'Show Full UI' : 'Simplify UI'}
            </button>
          </div>

          {/* Bottom Nav */}
          <div className={`fixed bottom-0 left-0 right-0 ${theme === 'light' ? 'bg-white border-gray-300' : 'bg-gray-900 border-gray-700'} border-t flex justify-around py-4`}>
            <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('dashboard')}>
              <Home className="w-6 h-6" style={{color: colors.primary}} />
              <span className="text-xs" style={{color: colors.primary}}>Home</span>
            </button>
            <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('analytics')}>
              <BarChart3 className={`w-6 h-6 ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`} />
              <span className={`text-xs ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>Analytics</span>
            </button>
            <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('settings')}>
              <Settings className={`w-6 h-6 ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`} />
              <span className={`text-xs ${theme === 'light' ? 'text-gray-600' : 'text-gray-400'}`}>Settings</span>
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Focus Mode
  if (currentScreen === 'focusMode') {
    const scale = 1 + Math.sin(breathingPhase * 0.0628) * 0.2;
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-8">
        <h1 className="text-sm text-gray-400 mb-12">Focus Mode</h1>
        <div className="text-7xl font-light mb-4">{formatTime(focusTimer)}</div>
        <div className="text-gray-400 mb-16">Task: Study Algorithms</div>
        
        <div className="mb-8">
          <div className="text-gray-400 text-sm mb-4 text-center">Breathing Animation</div>
          <div 
            className="w-40 h-40 rounded-full border-2 border-blue-400 transition-transform duration-100"
            style={{ transform: `scale(${scale})` }}
          ></div>
        </div>

        <button 
          onClick={() => {
            setIsTimerRunning(false);
            setCurrentScreen('dashboard');
          }}
          className="border-2 border-white px-16 py-3 rounded-full hover:bg-white hover:text-black transition-all"
        >
          Stop
        </button>
      </div>
    );
  }

  // Cognitive Alert
  if (currentScreen === 'cognitiveAlert') {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-8">
        <div className="bg-white text-gray-900 rounded-3xl p-8 max-w-sm">
          <div className="flex justify-center mb-4">
            <AlertTriangle className="w-16 h-16 text-red-500" strokeWidth={1.5} />
          </div>
          <h2 className="text-xl font-semibold mb-2 text-center">
            ⚠️ Your cognitive load is getting too high
          </h2>
          <p className="text-gray-600 text-center mb-6">Suggestion: 2-minute break</p>
          <div className="flex gap-3 mb-4">
            <button 
              onClick={() => {
                setCurrentScreen('microBreak');
                setBreakTimer(120);
                setLastBreakTime(Date.now());
              }}
              className="flex-1 border-2 border-gray-900 text-gray-900 py-3 rounded-full hover:bg-gray-900 hover:text-white transition-all"
            >
              Start Break
            </button>
            <button 
              onClick={() => {
                setCognitiveLoad(prev => Math.max(40, prev - 10));
                setCurrentScreen('dashboard');
              }}
              className="flex-1 border-2 border-gray-300 text-gray-600 py-3 rounded-full hover:bg-gray-100 transition-all"
            >
              Ignore
            </button>
          </div>
          <button className="w-full text-center text-gray-600 underline">Why?</button>
        </div>
      </div>
    );
  }

  // Micro Break
  if (currentScreen === 'microBreak') {
    const wavePhase = Math.sin(breathingPhase * 0.1) * 30;
    return (
      <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center p-8">
        <h1 className="text-xl font-light mb-8">Micro-break</h1>
        <div className="text-6xl font-light mb-16">{formatTime(breakTimer)}</div>
        
        <div className="mb-8">
          <svg width="200" height="100" viewBox="0 0 200 100" className="mb-4">
            <path
              d={`M 0,50 Q 50,${50 + wavePhase} 100,50 T 200,50`}
              fill="none"
              stroke="white"
              strokeWidth="2"
            />
          </svg>
        </div>

        <div className="text-xl mb-16">Breathe in... out...</div>

        <button 
          onClick={() => {
            setCognitiveLoad(25);
            setLastBreakTime(Date.now());
            setCurrentScreen('dashboard');
          }}
          className="border-2 border-white px-16 py-3 rounded-full hover:bg-white hover:text-black transition-all"
        >
          [ Skip ]
        </button>
      </div>
    );
  }

  // Analytics Screen
  if (currentScreen === 'analytics') {
    const weekData = [30, 45, 60, 55, 70, 65, 75];
    return (
      <div className="min-h-screen bg-black text-white p-6 pb-24">
        <div className="max-w-md mx-auto">
          <h1 className="text-2xl font-light mb-8 text-center">Analytics Screen</h1>
          
          <div className="mb-8">
            <h2 className="text-lg mb-4 text-gray-400">Weekly CLI Graph</h2>
            <div className="relative h-48 border-l-2 border-b-2 border-gray-700">
              <svg className="w-full h-full">
                {weekData.map((value, i) => {
                  const x = (i / 6) * 100;
                  const nextX = ((i + 1) / 6) * 100;
                  const y = 100 - (value / 80) * 100;
                  const nextY = i < 6 ? 100 - (weekData[i + 1] / 80) * 100 : y;
                  return (
                    <g key={i}>
                      <line
                        x1={`${x}%`}
                        y1={`${y}%`}
                        x2={`${nextX}%`}
                        y2={`${nextY}%`}
                        stroke="#10b981"
                        strokeWidth="2"
                      />
                      <circle cx={`${x}%`} cy={`${y}%`} r="4" fill="#10b981" />
                    </g>
                  );
                })}
              </svg>
            </div>
          </div>

          <div className="space-y-4 mb-8">
            <div className="flex justify-between">
              <span className="text-gray-400">Burnout Risk:</span>
              <span className="text-green-400">Low</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Avg Focus Session:</span>
              <span>32 min</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Peak Hours:</span>
              <span>6pm-10pm</span>
            </div>
          </div>

          <button 
            onClick={() => setCurrentScreen('insights')}
            className="w-full bg-green-600 hover:bg-green-700 text-white py-4 rounded-full transition-all"
          >
            View Insights
          </button>
        </div>

        {/* Bottom Nav */}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 flex justify-around py-4">
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('dashboard')}>
            <Home className="w-6 h-6 text-gray-400" />
            <span className="text-xs text-gray-400">Home</span>
          </button>
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('analytics')}>
            <BarChart3 className="w-6 h-6 text-blue-500" />
            <span className="text-xs text-blue-500">Analytics</span>
          </button>
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('permissions')}>
            <Settings className="w-6 h-6 text-gray-400" />
            <span className="text-xs text-gray-400">Settings</span>
          </button>
        </div>
      </div>
    );
  }

  // Insights Screen
  if (currentScreen === 'insights') {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white p-6 pb-24">
        <div className="max-w-md mx-auto">
          <button 
            onClick={() => setCurrentScreen('analytics')}
            className="text-blue-400 mb-6 hover:text-blue-300"
          >
            ← Back to Analytics
          </button>
          
          <h1 className="text-3xl font-light mb-8">Your Insights</h1>
          
          <div className="space-y-6">
            <div className="bg-gray-800 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-10 h-10 bg-green-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Productivity Pattern</h3>
                  <p className="text-gray-400 text-sm">Your focus peaks between 6pm-10pm. Consider scheduling your most demanding tasks during this window.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Break Patterns</h3>
                  <p className="text-gray-400 text-sm">You take breaks 40% less than recommended. Regular micro-breaks could improve your sustained focus by up to 25%.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-10 h-10 bg-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Brain className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Cognitive Load Trend</h3>
                  <p className="text-gray-400 text-sm">Your cognitive load has been steadily increasing over the past week. This week showed a 15% increase compared to last week.</p>
                </div>
              </div>
            </div>

            <div className="bg-gray-800 rounded-2xl p-6">
              <div className="flex items-start gap-4 mb-3">
                <div className="w-10 h-10 bg-yellow-600 rounded-full flex items-center justify-center flex-shrink-0">
                  <Activity className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Recommendation</h3>
                  <p className="text-gray-400 text-sm">Try the Pomodoro technique: 25 minutes of focused work followed by 5-minute breaks. This matches your natural rhythm.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Nav */}
        <div className="fixed bottom-0 left-0 right-0 bg-gray-900 border-t border-gray-700 flex justify-around py-4">
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('dashboard')}>
            <Home className="w-6 h-6 text-gray-400" />
            <span className="text-xs text-gray-400">Home</span>
          </button>
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('analytics')}>
            <BarChart3 className="w-6 h-6 text-blue-500" />
            <span className="text-xs text-blue-500">Analytics</span>
          </button>
          <button className="flex flex-col items-center gap-1" onClick={() => setCurrentScreen('permissions')}>
            <Settings className="w-6 h-6 text-gray-400" />
            <span className="text-xs text-gray-400">Settings</span>
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default NeuroSyncApp;